import {createAction, handleActions} from 'redux-actions';
import {call, put, takeLatest} from 'redux-saga/effects';
import createRequestSaga, {createRequestActionTypes} from '../../api/createRequestSaga';
import * as faqAPI from '../../api/admin/faq';
import {finishLoading, startLoading} from '../loading';

const INITIALIZE = 'faq/INITIALIZE';
const INITIALIZE_FILTER = 'faq/INITIALIZE_FILTER';
const INITIALIZE_FORM = 'faq/INITIALIZE_FORM';
const INITIALIZE_SAVE_RESULT = 'faq/INITIALIZE_SAVE_RESULT';
const CHANGE_FIELD = 'faq/CHANGE_FIELD';
const CHANGE_FORM_FIELD = 'faq/CHANGE_FORM_FIELD';
const SET_FORM_FROM_DETAIL = 'faq/SET_FORM_FROM_DETAIL';

const [READ_FAQ_LIST, READ_FAQ_LIST_SUCCESS, READ_FAQ_LIST_FAILURE] = createRequestActionTypes('faq/READ_FAQ_LIST');
const [READ_FAQ_DETAIL, READ_FAQ_DETAIL_SUCCESS, READ_FAQ_DETAIL_FAILURE] = createRequestActionTypes('faq/READ_FAQ_DETAIL');
const [CREATE_FAQ, CREATE_FAQ_SUCCESS, CREATE_FAQ_FAILURE] = createRequestActionTypes('faq/CREATE_FAQ');
const [UPDATE_FAQ, UPDATE_FAQ_SUCCESS, UPDATE_FAQ_FAILURE] = createRequestActionTypes('faq/UPDATE_FAQ');
const [DELETE_FAQ, DELETE_FAQ_SUCCESS, DELETE_FAQ_FAILURE] = createRequestActionTypes('faq/DELETE_FAQ');
const [BULK_UPLOAD_FAQ, BULK_UPLOAD_FAQ_SUCCESS, BULK_UPLOAD_FAQ_FAILURE] = createRequestActionTypes('faq/BULK_UPLOAD_FAQ');
const INITIALIZE_DELETE_RESULT = 'faq/INITIALIZE_DELETE_RESULT';
const INITIALIZE_BULK_UPLOAD_RESULT = 'faq/INITIALIZE_BULK_UPLOAD_RESULT';

export const initialize = createAction(INITIALIZE);
export const initializeFilter = createAction(INITIALIZE_FILTER);
export const initializeForm = createAction(INITIALIZE_FORM);
export const initializeSaveResult = createAction(INITIALIZE_SAVE_RESULT);

export const changeField = createAction(
    CHANGE_FIELD,
    ({ name, value }) => ({ name, value })
);

export const changeFormField = createAction(
    CHANGE_FORM_FIELD,
    ({ name, value }) => ({ name, value })
);

export const setFormFromDetail = createAction(
    SET_FORM_FROM_DETAIL,
    (detail) => detail
);

export const readFaqList = createAction(
    READ_FAQ_LIST,
    ({ keyword, categoryMainId, categorySubId, isActive, status, page }) =>
        ({ keyword, categoryMainId, categorySubId, isActive, status, page })
);

export const readFaqDetail = createAction(
    READ_FAQ_DETAIL,
    ({ id }) => ({ id })
);

export const createFaq = createAction(
    CREATE_FAQ,
    (payload) => payload
);

export const updateFaq = createAction(
    UPDATE_FAQ,
    (payload) => payload
);

export const deleteFaq = createAction(
    DELETE_FAQ,
    ({ id }) => ({ id })
);

export const initializeDeleteResult = createAction(INITIALIZE_DELETE_RESULT);

export const bulkUploadFaq = createAction(
    BULK_UPLOAD_FAQ,
    ({ file }) => ({ file })
);

export const initializeBulkUploadResult = createAction(INITIALIZE_BULK_UPLOAD_RESULT);

const readFaqListSaga = createRequestSaga(READ_FAQ_LIST, faqAPI.readFaqList);
const readFaqDetailSaga = createRequestSaga(READ_FAQ_DETAIL, faqAPI.readFaqDetail);

const normalizeAxiosError = (e) => {
    const status = e?.response?.status ?? null;
    const body = e?.response?.data;
    if (body && typeof body === 'object') {
        return { status, ...body };
    }
    return { status, detail: body ?? e?.message ?? null };
};

function* createFaqSaga(action) {
    yield put(startLoading(CREATE_FAQ));
    try {
        const response = yield call(faqAPI.createFaq, action.payload);
        yield put({ type: CREATE_FAQ_SUCCESS, payload: response.data });
    } catch (e) {
        yield put({ type: CREATE_FAQ_FAILURE, payload: normalizeAxiosError(e), error: true });
    }
    yield put(finishLoading(CREATE_FAQ));
}

function* updateFaqSaga(action) {
    yield put(startLoading(UPDATE_FAQ));
    try {
        const response = yield call(faqAPI.updateFaq, action.payload);
        yield put({ type: UPDATE_FAQ_SUCCESS, payload: response.data });
    } catch (e) {
        yield put({ type: UPDATE_FAQ_FAILURE, payload: normalizeAxiosError(e), error: true });
    }
    yield put(finishLoading(UPDATE_FAQ));
}

function* deleteFaqSaga(action) {
    yield put(startLoading(DELETE_FAQ));
    try {
        const response = yield call(faqAPI.deleteFaq, action.payload);
        yield put({ type: DELETE_FAQ_SUCCESS, payload: response.data ?? { id: action.payload.id } });
    } catch (e) {
        yield put({ type: DELETE_FAQ_FAILURE, payload: normalizeAxiosError(e), error: true });
    }
    yield put(finishLoading(DELETE_FAQ));
}

function* bulkUploadFaqSaga(action) {
    yield put(startLoading(BULK_UPLOAD_FAQ));
    try {
        const formData = new FormData();
        formData.append('file', action.payload.file);
        const response = yield call(faqAPI.bulkUploadFaq, formData);
        yield put({ type: BULK_UPLOAD_FAQ_SUCCESS, payload: response.data ?? true });
    } catch (e) {
        yield put({ type: BULK_UPLOAD_FAQ_FAILURE, payload: normalizeAxiosError(e), error: true });
    }
    yield put(finishLoading(BULK_UPLOAD_FAQ));
}

export function* faqSaga() {
    yield takeLatest(READ_FAQ_LIST, readFaqListSaga);
    yield takeLatest(READ_FAQ_DETAIL, readFaqDetailSaga);
    yield takeLatest(CREATE_FAQ, createFaqSaga);
    yield takeLatest(UPDATE_FAQ, updateFaqSaga);
    yield takeLatest(DELETE_FAQ, deleteFaqSaga);
    yield takeLatest(BULK_UPLOAD_FAQ, bulkUploadFaqSaga);
}

const emptyForm = {
    title: '',
    question: '',
    answer: '',
    categoryMainId: null,
    categorySubId: null,
    changeReason: '',
    isActive: true
};

const initialState = {
    keyword: '',
    categoryMainId: null,
    categorySubId: null,
    isActive: null,
    status: null,
    page: 1,
    listResponse: null,
    listErrorResponse: null,
    pagination: null,
    detailResponse: null,
    detailErrorResponse: null,
    form: emptyForm,
    saveResponse: null,
    saveErrorResponse: null,
    deleteResponse: null,
    deleteErrorResponse: null,
    bulkUploadResponse: null,
    bulkUploadErrorResponse: null
};

const faq = handleActions(
    {
        [INITIALIZE]: () => initialState,
        [INITIALIZE_FILTER]: (state) => ({
            ...state,
            keyword: '',
            categoryMainId: null,
            categorySubId: null,
            isActive: null,
            status: null,
            page: 1
        }),
        [INITIALIZE_FORM]: (state) => ({
            ...state,
            form: emptyForm,
            saveResponse: null,
            saveErrorResponse: null,
            detailResponse: null,
            detailErrorResponse: null
        }),
        [INITIALIZE_SAVE_RESULT]: (state) => ({
            ...state,
            saveResponse: null,
            saveErrorResponse: null
        }),
        [CHANGE_FIELD]: (state, { payload: { name, value } }) => ({
            ...state,
            [name]: value
        }),
        [CHANGE_FORM_FIELD]: (state, { payload: { name, value } }) => ({
            ...state,
            form: { ...state.form, [name]: value }
        }),
        [SET_FORM_FROM_DETAIL]: (state, { payload: detail }) => ({
            ...state,
            form: {
                title: detail?.title || '',
                question: detail?.question || '',
                answer: detail?.answer || '',
                categoryMainId: detail?.category_main_id ?? detail?.categoryMainId ?? null,
                categorySubId: detail?.category_sub_id ?? detail?.categorySubId ?? null,
                changeReason: detail?.change_reason || detail?.changeReason || '',
                isActive: detail?.is_active !== undefined ? detail.is_active : (detail?.isActive !== undefined ? detail.isActive : true)
            }
        }),
        [READ_FAQ_LIST_SUCCESS]: (state, { payload: data }) => ({
            ...state,
            listResponse: data?.data?.content || data?.content || data?.data || [],
            listErrorResponse: null,
            pagination: data?.data?.pagination || data?.pagination || null
        }),
        [READ_FAQ_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            listResponse: null,
            listErrorResponse: error,
            pagination: null
        }),
        [READ_FAQ_DETAIL_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            detailResponse: response?.data || response,
            detailErrorResponse: null
        }),
        [READ_FAQ_DETAIL_FAILURE]: (state, { payload: error }) => ({
            ...state,
            detailResponse: null,
            detailErrorResponse: error
        }),
        [CREATE_FAQ_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            saveResponse: response?.data || response,
            saveErrorResponse: null
        }),
        [CREATE_FAQ_FAILURE]: (state, { payload: error }) => ({
            ...state,
            saveResponse: null,
            saveErrorResponse: error
        }),
        [UPDATE_FAQ_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            saveResponse: response?.data || response,
            saveErrorResponse: null
        }),
        [UPDATE_FAQ_FAILURE]: (state, { payload: error }) => ({
            ...state,
            saveResponse: null,
            saveErrorResponse: error
        }),
        [DELETE_FAQ_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            deleteResponse: response ?? true,
            deleteErrorResponse: null
        }),
        [DELETE_FAQ_FAILURE]: (state, { payload: error }) => ({
            ...state,
            deleteResponse: null,
            deleteErrorResponse: error
        }),
        [INITIALIZE_DELETE_RESULT]: (state) => ({
            ...state,
            deleteResponse: null,
            deleteErrorResponse: null
        }),
        [BULK_UPLOAD_FAQ_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            bulkUploadResponse: response ?? true,
            bulkUploadErrorResponse: null
        }),
        [BULK_UPLOAD_FAQ_FAILURE]: (state, { payload: error }) => ({
            ...state,
            bulkUploadResponse: null,
            bulkUploadErrorResponse: error
        }),
        [INITIALIZE_BULK_UPLOAD_RESULT]: (state) => ({
            ...state,
            bulkUploadResponse: null,
            bulkUploadErrorResponse: null
        })
    },
    initialState
);

export default faq;
